import { ArrowLeft, BadgeCheck, Bell, Bolt, Camera, Check, Copy, Layers, MapPin, Minus, Plus, ScanLine, Share2, ShoppingBag, Sun, Moon, Trash2, Zap } from "lucide-react";
import { IMG } from "@/data/vomarcData";

export const money = (n) => `₹${n.toLocaleString("en-IN")}`;

export function HomeScreen({ go, notifications, onBell, cartCount }) {
  const actions = [
    { id: "scan", label: "Scan", icon: ScanLine },
    { id: "design", label: "Design", icon: Layers },
    { id: "electrical", label: "Electrical", icon: Zap },
    { id: "shop", label: "Shop", icon: ShoppingBag },
  ];
  return (
    <div className="pp-view" data-testid="pp-home">
      <div className="pp-topbar">
        <div><span className="pp-greet">Good morning</span><h4 className="pp-h">Athul's home</h4></div>
        <button className="pp-bell" onClick={onBell} data-testid="pp-bell" aria-label="Notifications"><Bell size={17} />{notifications > 0 && <i data-testid="pp-bell-count">{notifications}</i>}</button>
      </div>
      <div className="pp-quick" data-testid="pp-quick-actions">
        {actions.map((a) => { const I = a.icon; return <button key={a.id} onClick={() => go(a.id)} data-testid={`pp-quick-${a.id}`}><I size={17} /><em>{a.label}</em></button>; })}
      </div>
      <button className="pp-project-card" onClick={() => go("track")} data-testid="pp-project-card">
        <img src={IMG.arRoom} alt="project" />
        <div><strong>3BHK Renovation</strong><span>Thrissur · In progress</span></div>
        <div className="pp-ring" style={{ "--pct": "62%" }}><em>62%</em></div>
      </button>
      <div className="pp-next" data-testid="pp-next-milestone"><MapPin size={13} /> Next: Primer coat · Tomorrow 10 AM</div>
      <h4 className="pp-h">Recent site photos</h4>
      <div className="pp-photos" data-testid="pp-recent-photos">
        {[IMG.workforce, IMG.scan, IMG.market].map((src, i) => <img key={src} src={src} alt={`site ${i}`} />)}
      </div>
      <button className="pp-btn primary" onClick={() => go("passport")} data-testid="pp-view-passport">View Passport</button>
      {cartCount > 0 && <span className="pp-note" data-testid="pp-home-cart-hint">{cartCount} item{cartCount > 1 ? "s" : ""} waiting in your cart.</span>}
    </div>
  );
}

export function NotificationsScreen({ back, items, onClear }) {
  return (
    <div className="pp-view" data-testid="pp-notifications">
      <button className="pp-back" onClick={back} data-testid="pp-notifications-back"><ArrowLeft size={14} /> Home</button>
      <h4 className="pp-h">Notifications</h4>
      {items.length ? items.map((n, i) => <div key={n.id} className="pp-notif" data-testid={`pp-notif-${i}`}><Bell size={13} /><span>{n.text}</span></div>) : <p className="pp-note">You're all caught up.</p>}
      {items.length > 0 && <button className="pp-btn" onClick={onClear} data-testid="pp-notifications-clear">Mark all read</button>}
    </div>
  );
}

export function PassportScreen({ back, toast }) {
  const rows = [
    ["Designs", "Living room v3, Kitchen v1"],
    ["Products", "Modular Sofa, Arc Lamp, Sage Emulsion"],
    ["Warranties", "Sofa 5 yrs (1,642 days left) · Paint 7 yrs"],
    ["Material batches", "AC-204 · Batch #KL-1187 verified"],
    ["Verified work stages", "Surface prep, Primer signed off"],
    ["Contractors", "Vendor A · Electrician K. Suresh"],
  ];
  return (
    <div className="pp-view" data-testid="pp-passport">
      <button className="pp-back" onClick={back} data-testid="pp-passport-back"><ArrowLeft size={14} /> Home</button>
      <h4 className="pp-h">Home Passport</h4>
      {rows.map(([k, v]) => <div key={k} className="pp-passport-block"><span>{k}</span><em>{v}</em></div>)}
      <div className="pp-row-btns">
        <button className="pp-btn" onClick={() => toast("Passport link copied")} data-testid="pp-passport-copy"><Copy size={14} /> Copy link</button>
        <button className="pp-btn primary" onClick={() => toast("Passport shared with buyer")} data-testid="pp-passport-share"><Share2 size={14} /> Share</button>
      </div>
      <p className="pp-note">Transfers to the next owner when you sell.</p>
    </div>
  );
}

export function ScanScreen({ state, progress, start, reset, go }) {
  return (
    <div className="pp-view" data-testid="pp-scan">
      <h4 className="pp-h">Scan your room</h4>
      {state === "idle" && (
        <>
          <div className="pp-scan-stage idle" data-testid="pp-scan-stage"><ScanLine size={40} /><em>Point the phone at any wall</em></div>
          <button className="pp-btn primary" onClick={start} data-testid="pp-start-scan"><Camera size={15} /> Start LiDAR scan</button>
        </>
      )}
      {state === "scanning" && (
        <div className="pp-scan-stage scanning" data-testid="pp-scan-animation">
          <div className="pp-scan-outline">{Array.from({ length: 24 }).map((_, i) => <span key={i} style={{ animationDelay: `${i * 0.08}s` }} />)}</div>
          <div className="pp-scan-progress" data-testid="pp-scan-progress"><i style={{ width: `${progress}%` }} /></div>
          <em>Scanning… {progress}% · {progress < 40 ? "walls" : progress < 75 ? "openings" : "electrical points"}</em>
        </div>
      )}
      {state === "done" && (
        <div className="pp-scan-done" data-testid="pp-scan-result">
          <div className="pp-scan-room"><span className="pp-dim-w">4.6 m</span><span className="pp-dim-h">3.4 m</span><span className="pp-dim-c">H 2.9 m</span></div>
          <div className="pp-stats" data-testid="pp-scan-stats">
            <div><strong>15.6 m²</strong><span>Floor area</span></div>
            <div><strong>96%</strong><span>Accuracy</span></div>
            <div><strong>2 · 1</strong><span>Windows · doors</span></div>
            <div><strong>6</strong><span>Electrical pts</span></div>
          </div>
          <div className="pp-row-btns">
            <button className="pp-btn" onClick={() => go("electrical")} data-testid="pp-open-electrical"><Zap size={14} /> Electrical</button>
            <button className="pp-btn primary" onClick={() => go("design")} data-testid="pp-open-in-design">Open in Design</button>
          </div>
          <button className="pp-link" onClick={reset} data-testid="pp-rescan">Scan again</button>
        </div>
      )}
    </div>
  );
}

export function ElectricalScreen({ back, layer, setLayer, toast }) {
  const boq = [["Switch points", 8, 420], ["Sockets (16A)", 6, 380], ["Light points", 5, 350], ["Cable run", "42 m", 65]];
  return (
    <div className="pp-view" data-testid="pp-electrical">
      <button className="pp-back" onClick={back} data-testid="pp-electrical-back"><ArrowLeft size={14} /> Scan</button>
      <h4 className="pp-h">Electrical plan</h4>
      <div className={`pp-room pp-elec ${layer}`} data-testid="pp-electrical-room">
        <img src={IMG.arRoom} alt="room" />
        {layer === "wiring" && <><i className="pp-wire w1" /><i className="pp-wire w2" /><i className="pp-wire w3" /><b className="pp-pt p1" /><b className="pp-pt p2" /><b className="pp-pt p3" /></>}
        {layer === "nodrill" && <><i className="pp-nodrill z1" /><i className="pp-nodrill z2" /><span className="pp-room-product">Red = no-drill zone</span></>}
      </div>
      <div className="pp-chips" data-testid="pp-electrical-layers">
        {[["wiring", "Wiring"], ["nodrill", "No-drill zones"], ["none", "Photo only"]].map(([id, label]) => (
          <button key={id} className={`pp-cat ${layer === id ? "on" : ""}`} onClick={() => setLayer(id)} data-testid={`pp-layer-${id}`}>{label}</button>
        ))}
      </div>
      <div className="pp-boq" data-testid="pp-boq">
        {boq.map(([k, q, r]) => <div key={k}><span>{k}</span><em>{q}</em><strong>{typeof q === "number" ? money(q * r) : money(42 * r)}</strong></div>)}
        <div className="total"><span>BOQ estimate</span><strong data-testid="pp-boq-total">{money(8 * 420 + 6 * 380 + 5 * 350 + 42 * 65)}</strong></div>
      </div>
      <button className="pp-btn primary" onClick={() => toast("BOQ sent for vendor bids")} data-testid="pp-boq-bids"><Bolt size={14} /> Send BOQ for bids</button>
    </div>
  );
}

export function DesignScreen({ wall, setWall, walls, product, setProduct, products, fitChecked, setFitChecked, lighting, setLighting, before, setBefore, saveLook, looks }) {
  return (
    <div className="pp-view" data-testid="pp-design">
      <div className={`pp-room ${lighting === "evening" ? "evening" : ""}`} data-testid="pp-design-room">
        <img src={IMG.arRoom} alt="room" />
        {!before && <span className="pp-room-tint" style={{ background: wall.v }} data-testid="pp-wall-tint" />}
        {!before && <span className="pp-room-product" data-testid="pp-room-product">{product.name}</span>}
        <span className="pp-ba" data-testid="pp-before-after-tag">{before ? "Before" : "After"}</span>
        {fitChecked && !before && (
          <span className={`pp-fit ${product.fit === "fits" ? "ok" : "bad"}`} data-testid="pp-fit-result">{product.fit === "fits" ? "Fits · 12 cm clearance" : "Blocks door"}</span>
        )}
      </div>
      <div className="pp-row-btns compact">
        <button className={`pp-toggle ${before ? "on" : ""}`} onClick={() => setBefore((b) => !b)} data-testid="pp-toggle-before">{before ? "Show after" : "Show before"}</button>
        <button className="pp-toggle" onClick={() => setLighting((l) => (l === "day" ? "evening" : "day"))} data-testid="pp-toggle-lighting">{lighting === "day" ? <><Moon size={13} /> Evening</> : <><Sun size={13} /> Day</>}</button>
      </div>
      <span className="pp-mini-label">Wall colour</span>
      <div className="pp-chips" data-testid="pp-wall-chips">
        {walls.map((w) => <button key={w.id} className={`pp-swatch ${wall.id === w.id ? "on" : ""}`} style={{ background: w.v }} onClick={() => setWall(w)} data-testid={`pp-wall-${w.id}`} aria-label={w.label} />)}
      </div>
      <span className="pp-mini-label">Swap product</span>
      <div className="pp-prod-row" data-testid="pp-design-products">
        {products.slice(0, 4).map((p) => (
          <button key={p.id} className={`pp-prod-mini ${product.id === p.id ? "on" : ""}`} onClick={() => { setProduct(p); setFitChecked(false); }} data-testid={`pp-swap-${p.id}`}><img src={p.img} alt={p.name} /><em>{p.name}</em></button>
        ))}
      </div>
      <div className="pp-row-btns">
        <button className="pp-btn" onClick={saveLook} data-testid="pp-save-look">Save look{looks > 0 ? ` (${looks})` : ""}</button>
        <button className="pp-btn primary" onClick={() => setFitChecked(true)} data-testid="pp-check-fit">Check fit</button>
      </div>
    </div>
  );
}

export function CartScreen({ back, cart, inc, dec, remove, total, mode, setMode, checkout, getBids }) {
  const modes = [["deliver", "Deliver only"], ["service", "With installation"], ["bids", "Vendor bids"]];
  return (
    <div className="pp-view" data-testid="pp-cart">
      <button className="pp-back" onClick={back} data-testid="pp-cart-back"><ArrowLeft size={14} /> Shop</button>
      <h4 className="pp-h">Cart · {cart.reduce((n, c) => n + c.qty, 0)} items</h4>
      <div className="pp-cart-items">
        {cart.length ? cart.map((p) => (
          <div key={p.id} className="pp-cart-item" data-testid={`pp-cart-item-${p.id}`}>
            <span>{p.name}</span>
            <div className="pp-qty"><button onClick={() => dec(p.id)} data-testid={`pp-qty-dec-${p.id}`} aria-label="decrease"><Minus size={12} /></button><b data-testid={`pp-qty-${p.id}`}>{p.qty}</b><button onClick={() => inc(p.id)} data-testid={`pp-qty-inc-${p.id}`} aria-label="increase"><Plus size={12} /></button></div>
            <em>{money(p.price * p.qty)}</em>
            <button className="pp-remove" onClick={() => remove(p.id)} data-testid={`pp-remove-${p.id}`} aria-label="remove"><Trash2 size={13} /></button>
          </div>
        )) : <p className="pp-note">Cart is empty. Add products first.</p>}
      </div>
      {cart.length > 0 && (
        <>
          <div className="pp-total" data-testid="pp-cart-total"><span>Total</span><strong>{money(total)}</strong></div>
          <span className="pp-mini-label">Checkout as</span>
          <div className="pp-chips" data-testid="pp-checkout-modes">
            {modes.map(([id, label]) => <button key={id} className={`pp-cat ${mode === id ? "on" : ""}`} onClick={() => setMode(id)} data-testid={`pp-mode-${id}`}>{label}</button>)}
          </div>
          {mode === "bids" ? (
            <button className="pp-btn primary" onClick={getBids} data-testid="pp-get-bids">Get vendor bids</button>
          ) : (
            <button className="pp-btn primary" onClick={checkout} data-testid="pp-buy-direct">{mode === "service" ? "Book with installation" : "Buy direct"}</button>
          )}
        </>
      )}
    </div>
  );
}

export function TrackScreen({ milestones, verified, verify, released, canRelease, release, workers, checkIn, qc, runQc }) {
  const pct = [0, 45, 75, 100][released];
  return (
    <div className="pp-view" data-testid="pp-track">
      <h4 className="pp-h">Execution tracker</h4>
      <div className="pp-workers" data-testid="pp-workers">
        {workers.map((w) => (
          <button key={w.id} className={`pp-worker ${w.in ? "in" : ""}`} onClick={() => checkIn(w.id)} data-testid={`pp-worker-${w.id}`}>
            <img src={w.img} alt={w.name} /><strong>{w.name}</strong><em>{w.in ? <><Check size={10} /> On site</> : "Check in"}</em>
          </button>
        ))}
      </div>
      <div className="pp-timeline" data-testid="pp-timeline">
        {milestones.map((m, i) => {
          const ok = verified.includes(m.id);
          return (
            <div key={m.id} className={`pp-ms ${ok ? "verified" : ""}`} data-testid={`pp-milestone-${m.id}`}>
              <img src={m.img} alt={m.label} />
              <div className="pp-ms-body">
                <strong>{m.label}</strong><span><MapPin size={11} /> {m.gps}</span>
                {ok ? <em className="pp-verified"><BadgeCheck size={12} /> Photo + barcode verified</em> : (i < 3 && <button className="pp-verify" onClick={() => verify(m.id)} data-testid={`pp-verify-${m.id}`}>Verify on site</button>)}
              </div>
            </div>
          );
        })}
      </div>
      <button className="pp-btn" onClick={runQc} disabled={qc === "running"} data-testid="pp-run-qc">{qc === "running" ? "AI checking…" : qc === "done" ? "AI QC: 98% match to design" : "Run AI quality check"}</button>
      <div className="pp-pay" data-testid="pp-payment-box">
        <div className="pp-pay-bar"><i style={{ width: `${pct}%` }} /></div>
        <span>{pct}% released · {money(Math.round(187000 * pct / 100))}</span>
        <button className="pp-btn primary" disabled={!canRelease} onClick={release} data-testid="pp-release-payment">Release payment {canRelease ? `(${milestones[released].tranche}%)` : ""}</button>
        {!canRelease && released < 3 && <p className="pp-note">Verify a milestone to enable release.</p>}
      </div>
    </div>
  );
}
